"use client";

import { useState } from "react";
import type { Member, PriorityQueueItem } from "@/data/types";
import type { TabName } from "@/components/Topbar";

// ============================================
// Sidebar — priority queue + member list
// ============================================

interface SidebarProps {
  members: Record<string, Member>;
  priorityQueue: readonly PriorityQueueItem[];
  currentMemberId: string;
  currentTab: TabName;
  onSelectMember: (memberId: string) => void;
}

function getRingClass(member: Member): string {
  if (member.trafficLight === "red") return "red-ring";
  if (member.trafficLight === "amber") return "amber-ring";
  if (member.churnRisk > 30) return "gold-ring";
  return "green-ring";
}

function getStatusColor(member: Member): string {
  if (member.trafficLight === "red") return "var(--red-soft)";
  if (member.trafficLight === "amber") return "var(--amber)";
  return "var(--green)";
}

export default function Sidebar({
  members,
  priorityQueue,
  currentMemberId,
  currentTab,
  onSelectMember,
}: SidebarProps) {
  const [filter, setFilter] = useState("");

  const filteredMembers = Object.values(members).filter((m) => {
    const q = filter.toLowerCase();
    return (
      m.name.toLowerCase().includes(q) ||
      (m.company && m.company.toLowerCase().includes(q)) ||
      (m.location && m.location.toLowerCase().includes(q)) ||
      (m.industry && m.industry.toLowerCase().includes(q))
    );
  });

  return (
    <aside className="sidebar">
      <div className="sidebar-section-label">Needs Attention</div>
      <div className="priority-queue">
        {priorityQueue.map((p) => {
          const m = members[p.memberId];
          if (!m) return null;
          const isActive =
            p.memberId === currentMemberId && currentTab === "dossier";
          return (
            <div
              key={p.memberId}
              className={`priority-item${isActive ? " active" : ""}`}
              data-member={p.memberId}
              onClick={() => onSelectMember(p.memberId)}
            >
              <div className={`priority-dot ${p.urgency}`}></div>
              <div className="priority-info">
                <div className="priority-name">{m.name}</div>
                <div className="priority-reason">{p.reason}</div>
              </div>
              <div className="priority-time">{p.time}</div>
            </div>
          );
        })}
      </div>
      <div className="member-list-section">
        <div className="sidebar-section-label" style={{ paddingTop: 14 }}>
          All Members
        </div>
        <div className="sidebar-search">
          <input
            type="text"
            className="sidebar-search-input"
            placeholder="Filter members..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <div className="member-list">
          {filteredMembers.map((m) => {
            const ringClass = getRingClass(m);
            const statusColor = getStatusColor(m);
            const isActive =
              m.id === currentMemberId && currentTab === "dossier";
            return (
              <div
                key={m.id}
                className={`member-item${isActive ? " active" : ""}`}
                data-member={m.id}
                onClick={() => onSelectMember(m.id)}
              >
                <div className={`member-avatar ${ringClass}`}>
                  {m.initials}
                </div>
                <div className="member-meta">
                  <div className="member-item-name">{m.name}</div>
                  <div className="member-item-detail">
                    {m.company || m.industry}
                    {m.location ? ` \u2014 ${m.location}` : ""}
                  </div>
                </div>
                <div
                  className="member-item-status"
                  style={{ background: statusColor }}
                ></div>
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
